import { toSpaceId } from "@circle-react/components/QuickPost/useQuickPostSpaceId";
import type {
  EditablePost,
  EditablePostLookup,
} from "@circle-react/components/QuickPostV3/data/types";
import { usePostDetails } from "@circle-react/contexts/postsDetailsContext";
import { usePostResource } from "@circle-react/hooks/posts/usePostResource";

import type { PostDetailRecord } from "@/react/lib/queries/posts/types";

type EditablePostState =
  | { status: "loading" }
  | { post: EditablePost; status: "ready" };

interface PostDetailsResult {
  isLoading?: boolean;
  postDetails?: Partial<PostDetailRecord>;
}

const mergeEditablePost = (
  basePost: EditablePost,
  postDetails: Partial<PostDetailRecord>,
): EditablePost => ({
  ...basePost,
  ...postDetails,
  meta_tag_attributes:
    postDetails.meta_tag_attributes === null
      ? {}
      : (postDetails.meta_tag_attributes ?? basePost.meta_tag_attributes),
});

const isEditablePost = (post: unknown): post is EditablePost =>
  Boolean(
    post &&
    typeof post === "object" &&
    "id" in post &&
    typeof post.id === "number" &&
    "slug" in post &&
    typeof post.slug === "string" &&
    "space_id" in post,
  );

export const useEditablePost = (
  stalePost: EditablePostLookup,
): EditablePostState => {
  const staleSpaceId = toSpaceId(stalePost.space_id);
  const postResource = usePostResource({
    spaceId: staleSpaceId,
    postSlug: stalePost.slug,
    queryOptions: { gcTime: 0 },
  });
  const postDetailsResult: PostDetailsResult = usePostDetails(stalePost.id);
  const basePost: EditablePost | null = isEditablePost(postResource.post)
    ? postResource.post
    : null;

  if (
    postResource.isInitialLoading ||
    postDetailsResult.isLoading ||
    !basePost
  ) {
    return { status: "loading" };
  }

  const postDetails = postDetailsResult.postDetails ?? {};
  const post = mergeEditablePost(basePost, postDetails);
  const spaceId = toSpaceId(post.space_id);

  if (!spaceId) {
    return { status: "loading" };
  }

  return { post: { ...post, space_id: spaceId }, status: "ready" };
};
